import { TableHeader } from '@/components/data-table/DataTable';
import { KeyInfoInterface } from '@/interfaces/KeyInfoInterface';
import { RestrictType } from '@/interfaces/RestrictType';
import { Model } from '@/models/Model';
import { ProductVariant } from '@/models/ProductVariant';
import { Category } from '@/models/Category';
import { ProductVariantStatusEnum, ProductVariantStatusKeyInfo } from '@/items/ProductVariantStatus';
import { Color } from '@/models/Color';
import { Size } from '@/models/Size';
import { Season } from '@/models/Season';
import { formatDate } from '@/support/String';
import { PurchaseOrder } from '@/models/PurchaseOrder';
import { Rules } from '@/support/ValidationRules';
import { Nmbr } from '@atabix/core';
import { thumbnailTransformer } from '@/support/ThumbnailTransformer';

// #region MODEL

export type PurchaseOrderItemIncludeType = 'purchase-order' | 'product-variant' | 'totals';

export interface PurchaseOrderItemFilterInterface {
  suppliers?: string[];
  received?: number;
  purchase_order?: string;
};

export type PurchaseOrderItemFilterInterfaceKeyType = keyof PurchaseOrderItemFilterInterface;

export class PurchaseOrderItem extends Model {
  protected $name = 'PurchaseOrderItem';

  protected $endpoint = '/purchase-order-items';

  protected $fillable = [];

  protected $includes: PurchaseOrderItemIncludeType[] = [];

  protected filters: PurchaseOrderItemFilterInterface = {};

  id?: string;

  buy_price?: string;

  purchase_order?: string | PurchaseOrder;

  product_variant?: string | ProductVariant;

  quantity?: string;

  quantity_received?: string;

  quantity_ordered?: string;

  quantity_outstanding?: string;

  total_price?: string;

  total_price_outstanding?: string;

  total_price_received?: string;

  date_received_at?: string;

  expected_at?: string;

  public include(includes: PurchaseOrderItemIncludeType | PurchaseOrderItemIncludeType[]): this {
    return super.include.call(this, includes);
  }

  public removeFilter(key: PurchaseOrderItemFilterInterfaceKeyType): this {
    return super.removeFilter.call(this, key);
  }

  public filter(filters: PurchaseOrderItemFilterInterfaceKeyType | PurchaseOrderItemFilterInterface, value?: any) {
    return super.filter.call(this, filters, value);
  }
}

// #endregion

// #region MODEL: KEYS

export type PurchaseOrderItemKeys = Exclude<keyof PurchaseOrderItem, keyof Model>;

// #endregion

// #region MODEL: KEY INFO

export class PurchaseOrderItemKeyInfoClass implements RestrictType<PurchaseOrderItemKeys, KeyInfoInterface, PurchaseOrderItemKeyInfoClass> {
  get id(): KeyInfoInterface {
    return {
      label: 'Id',
      labelShort: 'Id',
      placeholder: 'Id',
      rules: [],
    }
  }

  get buy_price(): KeyInfoInterface {
    return {
      label: 'Buy price',
      labelShort: 'Buy price',
      placeholder: 'Buy price',
      rules: [Rules.requiredWithMessageInEnglish],
    }
  }

  get purchase_order(): KeyInfoInterface {
    return {
      label: 'Purchase order',
      labelShort: 'Purchase order',
      placeholder: 'Purchase order',
      rules: [],
    }
  }

  get product_variant(): KeyInfoInterface {
    return {
      label: 'Product variant',
      labelShort: 'Product variant',
      placeholder: 'Variant',
      rules: [Rules.requiredWithMessageInEnglish],
    }
  }

  get expected_at(): KeyInfoInterface {
    return {
      label: 'Expected',
      labelShort: 'Expected',
      placeholder: 'Expected',
    }
  }

  get quantity(): KeyInfoInterface {
    return {
      label: 'Quantity',
      labelShort: 'Qty',
      placeholder: 'Quantity',
      rules: [Rules.requiredWithMessageInEnglish],
    }
  }

  get quantity_received(): KeyInfoInterface {
    return {
      label: 'Quantity received',
      labelShort: 'Qty received',
      placeholder: 'Quantity received',
      rules: [],
    }
  }

  get quantity_ordered(): KeyInfoInterface {
    return {
      label: 'Quantity ordered',
      labelShort: 'Qty ordered',
      placeholder: 'Quantity ordered',
      rules: [],
    }
  }

  get quantity_outstanding(): KeyInfoInterface {
    return {
      label: 'Quantity outstanding',
      labelShort: 'Qty outstanding',
      placeholder: 'Quantity outstanding',
      rules: [],
    }
  }

  get total_price(): KeyInfoInterface {
    return {
      label: 'Total',
      labelShort: 'Total',
      placeholder: 'Total',
      rules: [],
    }
  }

  get total_price_outstanding(): KeyInfoInterface {
    return {
      label: 'Total outstanding',
      labelShort: 'Total outstanding',
      placeholder: 'Total outstanding',
      rules: [],
    }
  }

  get total_price_received(): KeyInfoInterface {
    return {
      label: 'Total received',
      labelShort: 'Total received',
      placeholder: 'Total received',
      rules: [],
    }
  }

  get date_received_at(): KeyInfoInterface {
    return {
      label: 'Date received',
      labelShort: 'Date received',
      placeholder: 'Date received',
      rules: [],
    }
  }
}


export const PurchaseOrderItemKeyInfo = new PurchaseOrderItemKeyInfoClass();

// #endregion

// #region MODEL: TABLE HEADER

export type PurchaseOrderTableHeaderKeys = Exclude<PurchaseOrderItemKeys,
  // Exclude keys:
  'product_variant'
  | 'purchase_order'
>
// Add keys:
| 'image'
| 'merged_name_and_general_details'
| 'category'
| 'status'
| 'merged_color_and_size'
| 'merged_supplier_color_and_size'
| 'season';

export class PurchaseOrderItemTableHeaderClass {
  get id(): TableHeader {
    return {
      value: 'id',
      text: PurchaseOrderItemKeyInfo.id.labelShort,
    }
  }

  get upc(): TableHeader {
    return {
      value: 'upc',
      text: 'EAN',
      transform: (value: undefined, purchaseOrderItem: PurchaseOrderItem): string => {
        if (!purchaseOrderItem.product_variant || typeof purchaseOrderItem.product_variant !== 'object') { return ''; }
        const productVariant: ProductVariant = purchaseOrderItem.product_variant;
        const productEan: string = productVariant.upc ? productVariant.upc : '';
        return `<div class='custom-name'>${productEan}</div>`
      }
    }
  }

  get image(): TableHeader {
    return {
      value: 'image',
      text: 'Image',
      width: '1%',
      transform: (value: undefined, purchaseOrderItem: PurchaseOrderItem): string => {
        if (!purchaseOrderItem.product_variant || typeof purchaseOrderItem.product_variant !== 'object') { return ''; }
        const productVariant: ProductVariant = purchaseOrderItem.product_variant;

        if (!productVariant.image_url) { return ''; }
        const productImage: string = productVariant.image_url;

        return thumbnailTransformer(productImage);
      }
    }
  }

  get merged_name_and_general_details(): TableHeader {
    return {
      value: 'merged_name_and_general_details',
      text: 'Product name',
      action: '/varianten/{product_variant.id}',
      transform: (value: undefined, purchaseOrderItem: PurchaseOrderItem): string => {
        if (!purchaseOrderItem.product_variant || typeof purchaseOrderItem.product_variant !== 'object') { return ''; }
        const productVariant: ProductVariant = purchaseOrderItem.product_variant;

        const productName: string = productVariant.product_name ? productVariant.product_name : '';
        const productEan: string = '';
        const productSku: string = productVariant.sku ? productVariant.sku : '';
        const productSupplierStyle: string = productVariant.supplier_style_name ? productVariant.supplier_style_name : '';

        return `<div class='custom-name'>
  ${productName ? `<span><strong>${productName}</strong></span>` : ''}
  ${productEan ? `<span>${productEan}</span>` : ''}
  ${productSku ? `<span class='text-grey'>${productSku}</span>` : ''}
  ${productSupplierStyle ? `<span class='text-light-grey'>${productSupplierStyle}</span>` : ''}
</div>`;
      }
    }
  }

  get category(): TableHeader {
    return {
      value: 'category',
      text: 'Category',
      transform: (value: undefined, purchaseOrderItem: PurchaseOrderItem): string => {
        if (!purchaseOrderItem.product_variant || typeof purchaseOrderItem.product_variant !== 'object') { return ''; }
        const productVariant: ProductVariant = purchaseOrderItem.product_variant;

        const productCategories: Category[] = productVariant.categories ? productVariant.categories : [];

        const productCategoryNames: string[] = productCategories
          .map((category) => category.name)
          .filter((categoryName) => !!categoryName) as string[];

        return productCategoryNames.join(', ');
      }
    }
  }

  get status(): TableHeader {
    return {
      value: 'status',
      text: 'Status',
      transform: (value: undefined, purchaseOrderItem: PurchaseOrderItem): string => {
        if (!purchaseOrderItem.product_variant || typeof purchaseOrderItem.product_variant !== 'object') { return ''; }
        const productVariant: ProductVariant = purchaseOrderItem.product_variant;

        const productStatus: ProductVariantStatusEnum = (productVariant.status ? productVariant.status : ProductVariantStatusEnum.INACTIVE) as ProductVariantStatusEnum;
        const productStatusLabel = ProductVariantStatusKeyInfo[productStatus] ? ProductVariantStatusKeyInfo[productStatus].labelShort : productStatus;

        return `<span class='statusPill statusPill--${productStatus}'>${productStatusLabel}</span>`;
      }
    }
  }

  get merged_color_and_size(): TableHeader {
    return {
      value: 'merged_color_and_size',
      text: 'Color / Size',
      transform: (value: undefined, purchaseOrderItem: PurchaseOrderItem): string => {
        if (!purchaseOrderItem.product_variant || typeof purchaseOrderItem.product_variant !== 'object') { return ''; }
        const productVariant: ProductVariant = purchaseOrderItem.product_variant;

        let productColor: string | Color = productVariant.color ? productVariant.color : '';
        if (typeof productColor !== 'string') {
          productColor = productColor.name ? productColor.name : '';
        }

        let productSize: string | Size = productVariant.size ? productVariant.size : '';
        if (typeof productSize !== 'string') {
          productSize = productSize.name ? productSize.name : '';
        }

        return [productColor, productSize].filter(item => item).join(' / ');
      }
    }
  }

  get merged_supplier_color_and_size(): TableHeader {
    return {
      value: 'merged_supplier_color_and_size',
      text: 'Supplier color / size',
      transform: (value: undefined, purchaseOrderItem: PurchaseOrderItem): string => {
        if (!purchaseOrderItem.product_variant || typeof purchaseOrderItem.product_variant !== 'object') { return ''; }
        const productVariant: ProductVariant = purchaseOrderItem.product_variant;

        let supplierColor: string = productVariant.supplier_color ? productVariant.supplier_color : '';

        let supplierSize: string = '';

        if (productVariant.supplier_size && typeof productVariant.supplier_size === 'string') {
          supplierSize = productVariant.supplier_size;
        } else if (productVariant.supplier_size && typeof productVariant.supplier_size !== 'string') {
          supplierSize = productVariant.supplier_size.name || '';
        }

        return [supplierColor, supplierSize].filter(item => item).join(' / ');
      }
    }
  }

  get season(): TableHeader {
    return {
      value: 'season',
      text: 'Season',
      transform: (value: undefined, purchaseOrderItem: PurchaseOrderItem): string => {
        if (!purchaseOrderItem.product_variant || typeof purchaseOrderItem.product_variant !== 'object') { return ''; }
        const productVariant: ProductVariant = purchaseOrderItem.product_variant;

        let productSeason: string | Season = productVariant.season ? productVariant.season : '';
        if (typeof productSeason !== 'string') {
          productSeason = productSeason.name ? productSeason.name : '';
        }

        return productSeason;
      }
    }
  }

  get expected_at(): TableHeader {
    return {
      value: 'expected_at',
      text: PurchaseOrderItemKeyInfo.expected_at.labelShort,
      align: 'right',
      class: 'text-right',
    }
  }

  get quantity(): TableHeader {
    return {
      value: 'quantity',
      text: PurchaseOrderItemKeyInfo.quantity.labelShort,
      align: 'right',
      class: 'text-right',
    }
  }

  get quantity_ordered(): TableHeader {
    return {
      value: 'quantity_ordered',
      text: PurchaseOrderItemKeyInfo.quantity_ordered.labelShort,
      align: 'right',
      class: 'text-right',
    }
  }

  get quantity_outstanding(): TableHeader {
    return {
      value: 'quantity_outstanding',
      text: PurchaseOrderItemKeyInfo.quantity_outstanding.labelShort,
      align: 'right',
      class: 'text-right',
    }
  }

  get quantity_received(): TableHeader {
    return {
      value: 'quantity_received',
      text: PurchaseOrderItemKeyInfo.quantity_received.labelShort,
      align: 'right',
      class: 'text-right',
    }
  }


  get buy_price(): TableHeader {
    return {
      value: 'buy_price',
      text: PurchaseOrderItemKeyInfo.buy_price.labelShort,
      class: 'text-right',
      align: 'right',
      transform: (buy_price: number) => `<div class="text-right"><span class="d-block white-space-no-wrap">${Nmbr.toCurrency(buy_price ? buy_price : 0)}</span></div>`,
    }
  }

  get total_price(): TableHeader {
    return {
      value: 'total_price',
      text: PurchaseOrderItemKeyInfo.total_price.labelShort,
      class: 'text-right',
      align: 'right',
      transform: (total_price: number) => `<div class="text-right"><span class="d-block font-semibold white-space-no-wrap">${Nmbr.toCurrency(total_price ? total_price : 0)}</span></div>`,
      currencyFormat: true,
    }
  }

  get total_price_outstanding(): TableHeader {
    return {
      value: 'total_price_outstanding',
      text: PurchaseOrderItemKeyInfo.total_price_outstanding.labelShort,
      class: 'text-right',
      align: 'right',
      transform: (total_price_outstanding: number) => `<div class="text-right"><span class="d-block font-semibold white-space-no-wrap">${total_price_outstanding ? total_price_outstanding : 0}</span></div>`,
    }
  }

  get total_price_received(): TableHeader {
    return {
      value: 'total_price_received',
      text: PurchaseOrderItemKeyInfo.total_price_received.labelShort,
      class: 'text-right',
      align: 'right',
      transform: (total_price_received: number) => `<div class="text-right"><span class="d-block font-semibold white-space-no-wrap">${total_price_received ? total_price_received : 0}</span></div>`,
    }
  }

  get date_received_at(): TableHeader {
    return {
      value: 'date_received_at',
      text: PurchaseOrderItemKeyInfo.date_received_at.labelShort,
      class: 'text-right',
      align: 'right',
      transform: (date: string) => date ? formatDate(date, 'dd-LL-yyyy') : '',
    }
  }
}

export const PurchaseOrderItemTableHeader = new PurchaseOrderItemTableHeaderClass();

// #endregion
